/**
 * Aba "Arquivos" da ficha da cliente, idêntica à do legado (`admin.html:2622-2630`, `6405-6414`,
 * `7753-7767`; linhas 195, 196 e 197 do inventário).
 *
 * Duas listas, "Arquivos enviados por você" (subpasta `da-lu`) e "Arquivos enviados pela cliente"
 * (`da-cliente`), cada uma lendo a pasta do lead e a antiga; e o envio de um arquivo da Lu para a
 * cliente, que sobrescreve o de mesmo nome.
 *
 * O ARQUIVO ESCOLHIDO E A LINHA DE STATUS SOBREVIVEM À TROCA DE ABA, como no legado: o
 * `carregarArquivos` só refaz as duas listas (`admin.html:6411-6414`) e não toca no
 * `input-upload-lu` nem no `status-upload-lu`, que são DOM da página inteira. Por isso os dois
 * moram na memória da página (`comum-a3/memoria-da-pagina.ts`), e não no componente, que desmonta
 * quando a aba sai da tela.
 *
 * Território da frente A3. A moldura (`../index.tsx`) importa esta aba pelo nome `AbaArquivos` e
 * entrega `idCliente` e `ficha` (contrato `PropsDaAba`, em `../comum-ficha/tipos.ts`).
 */

// Estado, efeito de layout, memória de função e referência do React.
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
// As peças do Chassi.
import { Botao, Campo } from '@complexity/ui';
// O caminho da pasta do lead e o nome aceito pelo Storage, portados do legado.
import { caminhoArquivosCliente, sanitizarNomeArquivo } from '@studiolu/dados';
// As chamadas ao Storage da ficha.
import { enviarArquivo, pastasDaSubpasta } from '@studiolu/dados/areas/ficha-arquivos';
// A fonte de dados do painel.
import { usePainel } from '../../../contexto.js';
// O parágrafo do status, peça comum (o relógio aqui é o da memória da página).
import { LinhaDeStatus } from '../../../comum/status-temporario.js';
// O contrato da aba e o contrato de carga (a moldura lê antes de mostrar, e relê no reclique).
import type { FichaAberta, PropsDaAba } from '../comum-ficha/tipos.js';
import { useCarregadorDaAba } from '../comum-ficha/carregador.js';
// As peças da A3: lista de arquivos, recorte por papel, a trava sem ficha e o escopo estável.
import { ligacaoVazia, ListaDeArquivos } from './comum-a3/lista-de-arquivos.js';
import { useRecortePorPapel } from './comum-a3/papel.js';
import { AbaSemFicha } from './comum-a3/sem-ficha.js';
import { useEscopoEstavel } from './comum-a3/escopo.js';
import { criarMemoria, criarStatusDaPagina, useMemoria } from './comum-a3/memoria-da-pagina.js';

// O limite de envio do legado: 10 MB (`TAMANHO_MAX`, `admin.html:7716`).
const TAMANHO_MAX = 10 * 1024 * 1024;

// O `input-upload-lu` e o `status-upload-lu` do legado: DOM da página, que nada limpa ao trocar de
// aba nem ao abrir outra cliente (ver `comum-a3/memoria-da-pagina.ts`).
const arquivoEscolhido = criarMemoria<File | null>(null);
const statusDoEnvio = criarStatusDaPagina();

export function AbaArquivos({ ficha }: PropsDaAba) {
  // Sem a ficha aberta, a aba não consulta nada (ver `sem-ficha.tsx`).
  if (!ficha) return <AbaSemFicha aba="Arquivos" />;
  return <ConteudoArquivos ficha={ficha} />;
}

function ConteudoArquivos({ ficha }: { readonly ficha: FichaAberta }) {
  const { dados } = usePainel();
  const { somenteLeitura } = useRecortePorPapel();
  // O escopo muda só quando muda a cliente ou o lead (ver `comum-a3/escopo.ts`).
  const escopo = useEscopoEstavel(ficha);
  // O arquivo escolhido no seletor, lido no clique como o `input.files[0]` do legado. Ele fica na
  // memória da página: sair da aba e voltar encontra o mesmo arquivo escolhido, como no legado.
  const [arquivo, setArquivo] = useMemoria(arquivoEscolhido);
  // Trocar a chave recria o seletor vazio, que é o `input.value = ''` do legado (`7765`).
  const [chaveDoSeletor, setChaveDoSeletor] = useState(0);
  // As duas listas, comandadas por esta aba, como `carregarArquivos` faz no legado (`6411-6414`).
  const listaDaLu = useRef(ligacaoVazia());
  const listaDaCliente = useRef(ligacaoVazia());
  // A linha de status também dura a página: o "Enviado!" some pelo relógio, mesmo com a aba fora.
  const [status] = useMemoria(statusDoEnvio.memoria);
  const { mostrar, fixar } = statusDoEnvio;
  // A caixa em volta do `Campo`, para devolver o arquivo escolhido ao `input` ao voltar para a aba.
  const caixaDoSeletor = useRef<HTMLDivElement>(null);

  // A carga da aba: as duas listas, na ordem do `carregarArquivos` do legado (`admin.html:6411-6414`).
  // A moldura roda isto ANTES de mostrar a aba, e de novo a cada toque no botão dela.
  const carregar = useCallback(async () => {
    await listaDaLu.current.ler();
    await listaDaCliente.current.ler();
  }, []);
  useCarregadorDaAba(carregar);

  // O `input` recém-desenhado nasce vazio; o legado nunca perdeu o arquivo, porque o `input` é o
  // mesmo elemento o tempo todo. `DataTransfer` é a única forma de escrever `input.files`.
  useLayoutEffect(() => {
    const seletor = caixaDoSeletor.current?.querySelector('input[type="file"]');
    if (!(seletor instanceof HTMLInputElement)) return;
    const pacote = new DataTransfer();
    if (arquivo) pacote.items.add(arquivo);
    seletor.files = pacote.files;
  }, [arquivo, chaveDoSeletor]);

  // "Enviar arquivo pra essa cliente" (`admin.html:7753-7767`). O botão não trava durante o
  // envio, como no legado: a peça tem `carregando`, e usá-la seria comportamento novo.
  const enviar = async () => {
    if (!arquivo) {
      fixar('Escolhe um arquivo primeiro.');
      return;
    }
    if (arquivo.size > TAMANHO_MAX) {
      fixar('Arquivo muito grande. O limite é 10 MB.');
      return;
    }
    fixar('Enviando...');
    // A pasta do lead (ou a antiga, sem lead) e o nome sem acento nem caractere especial.
    const caminho = `${caminhoArquivosCliente(escopo, 'da-lu')}/${sanitizarNomeArquivo(arquivo.name)}`;
    // `upsert: true`: o mesmo nome substitui, como no legado.
    const resultado = await enviarArquivo(dados, caminho, arquivo, { upsert: true });
    // Este ponto do legado TRADUZ a mensagem (`traduzirErro`, `7763`).
    if (!resultado.ok) {
      fixar(resultado.erro.frase);
      return;
    }
    mostrar('Enviado!');
    setArquivo(null);
    setChaveDoSeletor((n) => n + 1);
    // O legado relê SÓ a lista "por você" depois do envio (`listarPasta('da-lu', ...)`, `7766`).
    await listaDaLu.current.ler();
  };

  return (
    <section className="flex flex-col gap-cx-3">
      <h3 className="text-cx-base font-semibold text-texto">Arquivos enviados por você</h3>
      <ListaDeArquivos pastas={pastasDaSubpasta(escopo, 'da-lu')} ligacao={listaDaLu.current} testid="lista-da-lu" />
      {/* O rótulo fica visível porque o `Campo` do Chassi sempre o desenha; no legado ele era só
          `aria-label` (divergência de peça ACEITA pelo fundador em 19/09/2026). Consulta:
          desabilitado (`3394`). A caixa em volta existe para o efeito achar o `input`: o `Campo` é
          componente de função, e no React 18 ele não repassa `ref`. */}
      <div ref={caixaDoSeletor}>
        <Campo
          key={chaveDoSeletor}
          type="file"
          rotulo="Arquivo para enviar pra essa cliente"
          disabled={somenteLeitura}
          onChange={(evento) => setArquivo(evento.target.files?.[0] ?? null)}
        />
      </div>
      {/* Consulta não vê botão de ação (`admin.html:3395`). */}
      {!somenteLeitura && (
        <div>
          <Botao onClick={() => void enviar()}>Enviar arquivo pra essa cliente</Botao>
        </div>
      )}
      <LinhaDeStatus texto={status} testid="status-upload-lu" />
      <h3 className="text-cx-base font-semibold text-texto">Arquivos enviados pela cliente</h3>
      <ListaDeArquivos pastas={pastasDaSubpasta(escopo, 'da-cliente')} ligacao={listaDaCliente.current} testid="lista-da-cliente" />
    </section>
  );
}
